import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import { optionalOneOf, optionalString, searchPatch, optionalSearch } from "@/lib/search";
import { SearchField } from "@/components/app/patterns/search-field";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { isEmployeeUser, isNoLoginEmail, isPortalOnlyRole, errorWords } from "@alm/domain";
import { facilityAdminIds } from "@alm/policy";
import { toast } from "sonner";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { useEveryRow } from "@/lib/query/every-row";
import { orpc, orpcClient } from "@/lib/orpc";
import { useCanOrganizationWide, useCanWithoutBuilding, usePolicy } from "@/lib/policy";
import { guardRoute } from "@/lib/policy/route-guard";
import {
  createdMessage,
  StaffForm,
  staffFormToInput,
} from "@/routes/_authenticated/_administration/employees/-components/staff-form";
import { StaffRowActions } from "@/routes/_authenticated/_administration/employees/-components/staff-row-actions";
import { NoMatches } from "@/routes/_authenticated/_administration/settings/-components/no-matches";

export const Route = createFileRoute("/_authenticated/_administration/settings/users")({
  validateSearch: (
    search: Record<string, unknown>,
  ): { q?: string; kind?: Exclude<Kind, "all">; building?: string } => ({
    q: optionalSearch(search.q),
    kind: optionalOneOf(["employee", "non_employee"], search.kind),
    building: optionalString(search.building) || undefined,
  }),
  beforeLoad: ({ context }) => guardRoute(context.me, "user-management"),
  component: UserManagement,
});

const KINDS = [
  { value: "all", label: "Everyone" },
  { value: "employee", label: "Employees" },
  { value: "non_employee", label: "Not employees" },
] as const;

type Kind = (typeof KINDS)[number]["value"];

/** What a row is: a candidate is a candidate, not "member", and an
 *  administrator is named by the role they hold rather than by their
 *  membership row. */
const ROLE_LABELS: Record<string, string> = {
  super_admin: "Owner",
  staff: "Staff",
  platform_admin: "Platform admin",
  applicant: "Candidate",
  proxy: "Family / POA",
  physician: "Physician",
  partner: "Partner",
  demo: "Demo",
};

const roleLabel = (person: { role: string; orgRole: string | null }) =>
  ROLE_LABELS[person.role] ?? person.orgRole ?? person.role;

/** The whole list of logins, not just employees: volunteers, family and vendors must not be counted
 * or chased by HR. The switch decides which one a person is. */
function UserManagement() {
  const queryClient = useQueryClient();
  const canUpdate = useCanWithoutBuilding("staff:update");
  const orgCreate = useCanWithoutBuilding("staff:create");
  // Deleting an account is the organization's own door, whatever a building role holds.
  const canDelete = useCanOrganizationWide("organization:manage-members");
  // A building's admin makes and edits logins for their own buildings' staff;
  // the server decides which rows, and says so on each (`facilityAdminManages`).
  const adminOf = facilityAdminIds(usePolicy().subject);
  const canCreate = orgCreate || adminOf.length > 0;
  const { q, kind: kindParam, building: buildingParam } = Route.useSearch();
  const kind: Kind = kindParam ?? "all";
  const building = buildingParam ?? "all";
  const patch = searchPatch(useNavigate({ from: Route.fullPath }));
  const { text: search, change: setSearch } = useSearchParamText(q, (next) => patch({ q: next }));
  // The list is organization-wide; this narrows it to one building's people.
  const setKind = (next: Kind) => patch({ kind: next === "all" ? undefined : next });
  const setBuilding = (next: string) => patch({ building: next === "all" ? undefined : next });
  const [creating, setCreating] = useState(false);

  // Everybody, however many: the search and the header counts are over this list.
  const everyone = useEveryRow(
    orpc.administration.staff.list.key({ input: { includeInactive: true, kind: "all" } }),
    (page, call) =>
      orpcClient.administration.staff.list({ includeInactive: true, kind: "all", ...page }, call),
  );
  const facilities = useQuery(
    orpc.administration.facilities.list.queryOptions({ input: { includeInactive: false } }),
  );

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: orpc.administration.staff.key() });
    // The HR lists and this register change together.
    await queryClient.invalidateQueries({ queryKey: orpc.workforce.key() });
  };

  const setEmployee = useMutation(
    orpc.administration.staff.update.mutationOptions({
      onSuccess: async (person) => {
        toast.success(person.isEmployee ? "Marked as an employee" : "Marked as not an employee");
        await refresh();
      },
      onError: (error: Error) => toast.error(errorWords(error)),
    }),
  );
  const create = useMutation(
    orpc.administration.staff.create.mutationOptions({
      onSuccess: async (created) => {
        toast.success(createdMessage(created));
        setCreating(false);
        await refresh();
      },
      onError: (error: Error) => toast.error(errorWords(error)),
    }),
  );

  const all = everyone.data?.rows ?? [];
  // Prod's rule, not the flag alone: a candidate's flag is null and they are not staff.
  const employees = all.filter((person) => isEmployeeUser(person)).length;
  const others = all.length - employees;
  const needle = search.trim().toLowerCase();
  const rows = all
    .filter((person) =>
      kind === "all"
        ? true
        : kind === "employee"
          ? isEmployeeUser(person)
          : !isEmployeeUser(person),
    )
    .filter(
      (person) =>
        building === "all" || person.facilities.some((facility) => facility.id === building),
    )
    .filter(
      (person) =>
        !needle ||
        person.name.toLowerCase().includes(needle) ||
        (person.email ?? "").toLowerCase().includes(needle),
    );

  return (
    <>
      <PageHeader
        title="User management"
        meta={
          everyone.data
            ? `${String(employees)} employee${employees === 1 ? "" : "s"}, ${String(others)} other login${others === 1 ? "" : "s"}. HR keeps files for employees only.`
            : undefined
        }
        actions={
          <>
            <Button variant="outline" asChild>
              <Link to="/hr">Open the employees register</Link>
            </Button>
            {canCreate ? <Button onClick={() => setCreating(true)}>Add user</Button> : null}
          </>
        }
      />

      <Card className="gap-0 py-0">
        <CardHeader className="pt-4 pb-4">
          <CardTitle>Everyone with a login</CardTitle>
          <CardDescription>
            Volunteers, family helpers, vendors and portal logins live here alongside staff.
          </CardDescription>
        </CardHeader>

        {/* Under the header rather than beside it: the settings pane is narrow,
            and a search box in the header squeezes the title into a column of
            single words. */}
        <div className="flex flex-wrap items-center gap-2 border-t px-[18px] py-3">
          <SearchField
            value={search}
            onChange={setSearch}
            placeholder="Search name or email"
            className="min-w-48 flex-1"
          />
          <Select value={kind} onValueChange={(value) => setKind(value as Kind)}>
            <SelectTrigger className="w-44" aria-label="Which people">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {KINDS.map((entry) => (
                <SelectItem key={entry.value} value={entry.value}>
                  {entry.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={building} onValueChange={setBuilding}>
            <SelectTrigger className="w-48" aria-label="Which building">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All buildings</SelectItem>
              {(facilities.data ?? []).map((facility) => (
                <SelectItem key={facility.id} value={facility.id}>
                  {facility.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {everyone.isPending ? (
          <div className="flex flex-col gap-2 px-[18px] py-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : everyone.isError ? (
          <ReadFailed
            className="border-t px-[18px] py-4"
            message="Could not load the users."
            error={everyone.error}
            retrying={everyone.isFetching}
            onRetry={() => void everyone.refetch()}
          />
        ) : rows.length === 0 ? (
          <div className="border-t px-[18px] py-4">
            {needle ? (
              <NoMatches search={search} onClear={() => setSearch("")} />
            ) : (
              <p className="text-muted-foreground text-sm">
                Nobody in this group.{" "}
                <Button
                  type="button"
                  variant="link"
                  className="h-auto min-h-0 p-0"
                  onClick={() => {
                    patch({ kind: undefined, building: undefined });
                  }}
                >
                  Show everyone
                </Button>
              </p>
            )}
          </div>
        ) : (
          <ul className="flex flex-col border-t">
            {rows.map((person) => {
              const employee = isEmployeeUser(person);
              // A portal login is never staff, so there is nothing to switch.
              const portalOnly = isPortalOnlyRole(person.role);
              return (
                <li
                  key={person.id}
                  className="flex flex-wrap items-center justify-between gap-3 border-b px-[18px] py-3 last:border-b-0"
                >
                  <span className="flex min-w-0 flex-col">
                    {/* The employee record opens members only; a portal login has none. */}
                    {portalOnly ? (
                      <span className="truncate font-medium">{person.name}</span>
                    ) : (
                      <Link
                        to="/employees/$userId"
                        params={{ userId: person.id }}
                        className="hover:text-primary min-h-6 truncate py-0.5 font-medium"
                      >
                        {person.name}
                      </Link>
                    )}
                    <span className="text-muted-foreground truncate text-sm">
                      {/* A front desk person who never signs in carries a made-up
                          address so that emails stay unique; nobody needs to read it. */}
                      {isNoLoginEmail(person.email) ? "No login" : person.email}
                      {person.jobTitle ? `, ${person.jobTitle}` : ""}
                    </span>
                    {/* Where they work: the list is organization-wide, and a name
                        alone does not say which building it belongs to. */}
                    <span className="text-muted-foreground truncate text-sm">
                      {person.facilities.map((facility) => facility.name).join(", ") ||
                        "No building"}
                    </span>
                  </span>
                  <span className="flex max-w-full shrink-0 flex-wrap items-center gap-2">
                    <Badge variant="outline">{roleLabel(person)}</Badge>
                    {person.isActive ? null : <Badge variant="neutral">Inactive</Badge>}
                    {/* One control for the one fact: the switch says which it is
                        and changes it, where a badge and a button said it twice. */}
                    {(canUpdate || person.facilityAdminManages) && !portalOnly ? (
                      <span className="flex items-center gap-2">
                        <Switch
                          id={`employee-${person.id}`}
                          checked={employee}
                          disabled={setEmployee.isPending}
                          aria-label={`Employee: ${person.name}`}
                          onCheckedChange={(isEmployee) =>
                            setEmployee.mutate({ userId: person.id, isEmployee })
                          }
                        />
                        <label htmlFor={`employee-${person.id}`} className="text-sm">
                          Employee
                        </label>
                      </span>
                    ) : (
                      <Badge variant={employee ? "ok" : "neutral"}>
                        {employee ? "Employee" : "Not an employee"}
                      </Badge>
                    )}
                    {/* The account half of what the employees register can do; role,
                        facilities and details are on the record itself. */}
                    {portalOnly ? null : (
                      <StaffRowActions
                        user={person}
                        access
                        canReset={canUpdate || person.facilityAdminManages}
                        canDelete={canDelete}
                      />
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add user</DialogTitle>
          </DialogHeader>
          <StaffForm
            facilities={(facilities.data ?? []).filter(
              (facility) => orgCreate || adminOf.includes(facility.id),
            )}
            pending={create.isPending}
            onCancel={() => setCreating(false)}
            onSubmit={(values) => create.mutate(staffFormToInput(values))}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
